"use client";

import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import { BLUR_DATA } from "./blurData";
import { STEP_IMAGE_URL_PROPS, type StepImageEntry } from "./QuizStepImage";
import { shuffleImagePool } from "./shuffleImagePool";

const INTERVAL_MS = 5000;

/**
 * How many slides keep their <Image> mounted at once, most-recently-needed
 * first: [0] the active slide, [1] the slide after it (mounted a tick early
 * so its fetch starts before it's needed), [2] the outgoing slide still
 * mid-crossfade, [3] one tick of headroom.
 *
 * Invariant: INTERVAL_MS * (MOUNT_WINDOW - 2) must exceed the wrapper's fade
 * duration (`duration-[1200ms]` below — kept as a literal Tailwind class,
 * not a variable, so Tailwind's JIT scanner can see it). 10s of headroom
 * here against a 1.2s fade means eviction only ever reaches a slide that
 * stopped being active at least (MOUNT_WINDOW - 2) full ticks ago, so an
 * outgoing slide can never be unmounted mid-fade, which would turn the
 * crossfade into a hard cut.
 *
 * This bound is what lets the full 40-image-per-step pool be passed in (see
 * useQuizWizardController.ts) instead of a pre-sliced subset: without it, up
 * to 40 stacked, in-viewport <Image fill> elements would stay mounted for
 * the rest of the session, accumulating far more decoded bitmap memory than
 * this 4-slide window ever holds. A slide that gets re-shown after eviction
 * remounts from the browser's HTTP cache with BLUR_DATA as a placeholder
 * (next/image resets its own `blurComplete` state on mount).
 *
 * On every ordinary tick, the incoming slide was already mounted a full
 * INTERVAL_MS ago (pushed as `upcoming` on the previous tick), so there's
 * nothing to see even on a cold cache. The loop seam is the one place that
 * isn't automatic — see `pendingOrderRef` below, which exists specifically
 * to give the seam the same preload guarantee.
 *
 * Exported so tests can assert against it directly instead of a hardcoded
 * literal that happens to agree.
 */
export const MOUNT_WINDOW = 4;

function sameOrder(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((src, i) => src === b[i]);
}

/**
 * Auto-cycling, crossfading carousel for a quiz step's image pool — the
 * quiz-specific counterpart to app/(marketing)/_components/HeroImageCarousel.tsx.
 * Structurally mirrors it closely (state shape, interval mechanics, reshuffle
 * at the loop seam) but is a separate implementation: the two carousels are
 * deliberately not shared code, and this one differs where the quiz's layout
 * differs — a single shared `sizes` value (STEP_IMAGE_URL_PROPS) instead of
 * per-image landscape overrides, since PhotoSplit's panel is a fluid
 * width-relative box rather than the hero's fixed 4:5 box; and, as of the
 * MOUNT_WINDOW bound above, it deliberately DIVERGES from the hero carousel's
 * never-unmount behavior — the hero's pool is small enough that never
 * unmounting is fine, this one's isn't. Don't "restore parity" between the
 * two here; that would reintroduce unbounded mount growth.
 */
export function QuizStepImageCarousel({
  images,
  priority = false,
}: {
  images: StepImageEntry[];
  priority?: boolean;
}) {
  // Seeded once from the caller-computed initial order — never re-derived
  // from `images` on re-render, or the reshuffle below would be clobbered.
  const [order, setOrder] = useState(images);
  const [activeIndex, setActiveIndex] = useState(0);

  // The LCP element is fixed at first paint, not renegotiated every 5
  // seconds — captured once (as state, not a ref: reading a ref's `.current`
  // during render is disallowed by the react-hooks/refs lint rule) so
  // `priority` below always points at the image actually preloaded, even
  // after `order` reshuffles and a different image lands at index 0.
  const [initialFirstSrc] = useState(() => images[0]?.src);

  // Mirror `order`/`activeIndex` for synchronous reads inside the interval
  // callback below. The effect that drives the interval has an empty
  // dependency array (it must — it's a single long-lived interval, not one
  // restarted on every reshuffle), so it cannot close over `order` or
  // React's `prev` updater argument directly: that would either permanently
  // pin `order` to the value from the render that created the effect, or
  // force the reshuffle/reveal side effects to live inside the
  // `setActiveIndex` updater — which must stay pure (React may invoke it
  // more than once, e.g. under StrictMode, and a discarded/replayed call
  // must not have already mutated a ref or scheduled other state updates).
  // Kept in sync inline, in the same tick the interval fires, not via a
  // separate useEffect(() => ..., [order]) — that would still lag by one
  // commit relative to the interval callback that just ran.
  const orderRef = useRef(order);
  const activeIndexRef = useRef(activeIndex);

  // Next cycle's order, decided one tick before the seam (at the pool's last
  // index) rather than at the seam itself. This gives the next cycle's first
  // slide a genuine INTERVAL_MS of preload lead — deciding the reshuffle at
  // the seam instead (as an earlier version of this component did) means
  // that slide is drawn from a permutation nobody has seen yet, so it would
  // mount and go opacity-100 in the very same commit.
  const pendingOrderRef = useRef<StepImageEntry[] | null>(null);

  // Mounted slides, most-recently-needed first. Keyed by src (not index)
  // because the order reshuffles at each loop boundary — an index-keyed
  // structure would point at the wrong slides the moment that happens.
  // Ordered (not a Set) because eviction is by recency and the reshuffle
  // scrambles insertion order relative to display order. Initialised to the
  // first two so slide 0 paints immediately (priority) and slide 1 preloads
  // ahead of the first crossfade. Capped at MOUNT_WINDOW — see its comment
  // above for why slides are evicted rather than kept mounted forever.
  const [mounted, setMounted] = useState<readonly string[]>(
    () => order.slice(0, 2).map((img) => img.src),
  );

  useEffect(() => {
    const id = setInterval(() => {
      const currentOrder = orderRef.current;
      const len = currentOrder.length;
      if (len === 0) return;

      const next = (activeIndexRef.current + 1) % len;
      activeIndexRef.current = next;

      let nextOrder = currentOrder;
      if (next === 0 && pendingOrderRef.current) {
        // Loop seam: adopt the order decided a tick ago (see below), whose
        // first slide has already had a full INTERVAL_MS mounted in the
        // preload slot — not a fresh shuffle computed only now.
        nextOrder = pendingOrderRef.current;
        pendingOrderRef.current = null;
        orderRef.current = nextOrder;
        setOrder(nextOrder);
      }

      const incoming = nextOrder[next]!;
      let upcoming = nextOrder[(next + 1) % len];

      if (next === len - 1) {
        // Last slide of the current cycle: decide the next cycle now, so its
        // first slide gets a preload tick instead of mounting and going
        // opacity-100 in the same commit at the seam. `incoming` here is the
        // slide about to be shown last, so it is the no-repeat constraint:
        // the next cycle won't open on the same photo.
        const pending = shuffleImagePool(nextOrder, Math.random, incoming.src);
        pendingOrderRef.current = pending;
        upcoming = pending[0];
      }

      // Mount the incoming slide and the one after it, then evict past
      // MOUNT_WINDOW. `front` is deduped: a single-entry pool has
      // incoming === upcoming, and pushing it twice would make `mounted`
      // change identity on every tick forever.
      const front =
        upcoming && upcoming.src !== incoming.src
          ? [incoming.src, upcoming.src]
          : [incoming.src];

      setMounted((current) => {
        const merged = [
          ...front,
          ...current.filter((src) => !front.includes(src)),
        ].slice(0, MOUNT_WINDOW);
        return sameOrder(merged, current) ? current : merged;
      });

      setActiveIndex(next);
    }, INTERVAL_MS);

    return () => clearInterval(id);
  }, []);

  return (
    <div className="relative h-full w-full overflow-hidden" aria-hidden="true">
      {order.map((img, i) => (
        <div
          key={img.src}
          className={[
            "absolute inset-0 transition-opacity duration-[1200ms]",
            i === activeIndex ? "opacity-100" : "opacity-0",
          ].join(" ")}
        >
          {mounted.includes(img.src) && (
            <Image
              src={img.src}
              alt={img.alt}
              {...STEP_IMAGE_URL_PROPS}
              className="object-cover"
              style={img.objectPosition ? { objectPosition: img.objectPosition } : undefined}
              priority={priority && img.src === initialFirstSrc}
              {...(BLUR_DATA[img.src]
                ? { placeholder: "blur", blurDataURL: BLUR_DATA[img.src] }
                : {})}
            />
          )}
        </div>
      ))}
    </div>
  );
}
